import { Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useIsAuthenticated } from '@azure/msal-react'

import Layout from './components/Layout'
import RequirePermission from './components/RequirePermission'
import { OrganizationProvider } from './contexts/OrganizationContext'
import LoadingSpinner from './components/LoadingSpinner'
import LoginPage from './pages/LoginPage'
import OnboardingPage from './pages/OnboardingPage'
import OnboardingCallbackPage from './pages/OnboardingCallbackPage'
import DashboardPage from './pages/DashboardPage'
import InvoicesPage from './pages/InvoicesPage'
import InvoiceDetailPage from './pages/InvoiceDetailPage'
import ReportsPage from './pages/ReportsPage'
import UsersPage from './pages/UsersPage'
import OrganizationsPage from './pages/OrganizationsPage'
import CostCentersPage from './pages/CostCentersPage'
import CostCenterPermissionsPage from './pages/CostCenterPermissionsPage'
import AuditLogPage from './pages/AuditLogPage'
import SettingsPage from './pages/SettingsPage'
import AmbulanceOrderPage from './pages/AmbulanceOrderPage'
import AmbulanceOrderReviewPage from './pages/AmbulanceOrderReviewPage'
import AmbulanceOrderDetailPage from './pages/AmbulanceOrderDetailPage'
import PaymentTrafficPage from './pages/PaymentTrafficPage'
import BillingMaterialsPage from './pages/BillingMaterialsPage'
import BillingInvoicesPage from './pages/BillingInvoicesPage'
import DeductibleInvoicesPage from './pages/DeductibleInvoicesPage'
// DeductibleLinesPage removed — replaced by Excel export
import InvoiceInspectionPage from './pages/InvoiceInspectionPage'
import InvoiceAccessCodePage from './pages/InvoiceAccessCodePage'
import SecurityGroupMappingsPage from './pages/SecurityGroupMappingsPage'
import HelpPage from './pages/HelpPage'
import NotFoundPage from './pages/NotFoundPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated()
  const location = useLocation()

  if (!isAuthenticated) {
    // Store the return URL so user can be redirected after login
    return <Navigate
      to="/login"
      state={{ returnUrl: location.pathname + location.search }}
      replace
    />
  }

  return <>{children}</>
}

function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/onboarding/callback" element={<OnboardingCallbackPage />} />
        
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <OrganizationProvider>
                <Layout />
              </OrganizationProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          {/* Invoices — requires can_review_invoices or admin */}
          <Route path="invoices" element={<RequirePermission allow="canAccessInvoices"><InvoicesPage /></RequirePermission>} />
          {/* Access code search — open to all authenticated users */}
          <Route path="invoices/access/:accessCode" element={<InvoiceAccessCodePage />} />
          <Route path="invoices/:id/inspection" element={<RequirePermission allow="canAccessInvoices"><InvoiceInspectionPage /></RequirePermission>} />
          <Route path="invoices/:id" element={<RequirePermission allow="canAccessInvoices"><InvoiceDetailPage /></RequirePermission>} />
          {/* Reports — requires can_view_reports or admin */}
          <Route path="reports" element={<RequirePermission allow="canAccessReports"><ReportsPage /></RequirePermission>} />
          {/* Admin-only routes */}
          <Route path="users" element={<RequirePermission allow="isAdmin"><UsersPage /></RequirePermission>} />
          <Route path="organizations" element={<RequirePermission allow="isSystemAdmin"><OrganizationsPage /></RequirePermission>} />
          <Route path="cost-centers" element={<RequirePermission allow="isAdmin"><CostCentersPage /></RequirePermission>} />
          <Route path="cost-centers/:id/permissions" element={<RequirePermission allow="isAdmin"><CostCenterPermissionsPage /></RequirePermission>} />
          {/* Ambulance — requires ambulance permission */}
          <Route path="ambulance-orders" element={<RequirePermission allow="canAccessAmbulance"><AmbulanceOrderPage /></RequirePermission>} />
          <Route path="ambulance-orders/review" element={<RequirePermission allow="canReviewAmbulance"><AmbulanceOrderReviewPage /></RequirePermission>} />
          <Route path="ambulance-orders/:id" element={<RequirePermission allow="canAccessAmbulance"><AmbulanceOrderDetailPage /></RequirePermission>} />
          {/* Payment traffic — sys_admin only */}
          <Route path="payment-traffic" element={<RequirePermission allow="isSystemAdmin"><PaymentTrafficPage /></RequirePermission>} />
          <Route path="payment-traffic/materials/:billExportUuid" element={<RequirePermission allow="isSystemAdmin"><BillingMaterialsPage /></RequirePermission>} />
          <Route path="payment-traffic/invoices/:billExportUuid" element={<RequirePermission allow="isSystemAdmin"><BillingInvoicesPage /></RequirePermission>} />
          <Route path="payment-traffic/deductible-invoices" element={<RequirePermission allow="isSystemAdmin"><DeductibleInvoicesPage /></RequirePermission>} />
          <Route path="security-groups" element={<RequirePermission allow="isSystemAdmin"><SecurityGroupMappingsPage /></RequirePermission>} />
          <Route path="audit" element={<RequirePermission allow="isAdmin"><AuditLogPage /></RequirePermission>} />
          <Route path="settings" element={<SettingsPage />} />
          {/* Help — open to all authenticated users */}
          <Route path="help" element={<HelpPage />} />
        </Route>
        
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}

export default App
